import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CommaInput from './CommaInput';
import {
  Card,
  SectionTitle,
  SectionContent,
  FormRow,
  FormFieldGroup,
  FormFieldLabel,
  FormFieldDesc,
  InputUnit,
  CheckRow,
  CheckLabel,
  CheckboxCircle,
  Divider,
  DeductionCard,
  DeductionRate,
  DeductionLabel,
  DeductionDesc,
  ToggleSwitch,
  ToggleFieldRow,
  FieldBlock,
  RateButtonGroup,
  RateButton,
  OvertimeRow,
  AddRangeButton,
  AddRangeText,
  RealInput,
} from './SalarySettingStyled';

// ============================================
// 시간제 급여 기본 설정
// ============================================
// 법정 최저시급 (system_setting.MIN_WAGE_AMOUNT 와 동일한 값으로 유지 필요)
const MIN_WAGE_YEAR = 2026;
const MIN_WAGE_AMOUNT = 10320;

const DEDUCTION_OPTIONS = [
  { key: 'TAX_3_3', rate: '3.3%', label: '사업소득세' },
  { key: 'INSURANCE_4', rate: '4대보험', label: '근로소득세' },
  { key: 'CUSTOM', rate: '직접입력', label: '항목 커스텀' },
  { key: 'NONE', rate: '안함', label: '공제 없음' },
];

const DEDUCTION_DESCRIPTIONS = {
  TAX_3_3: '지급액의 3.3%를 원천징수합니다.(소득세 3% + 지방소득세 0.3%)',
  INSURANCE_4:
    '국민연금 4.5% · 건강보험 3.595% · 고용보험 0.9% · 장기요양보험 (건강보험료의 12.95%)을 공제합니다.',
  NONE: '공제하지 않습니다. 지급액이 그대로 실지급액이 됩니다.',
};

const HourlyBasicSection = ({ settings, onSettingsChange, employee }) => {
  const { t } = useTranslation();
  const deductionType = settings.deductionType || 'CUSTOM';
  const customDeductions = settings.customDeductions || [];

  const handleAddDeduction = () => {
    onSettingsChange('customDeductions', [
      ...customDeductions,
      { itemName: '', rate: 0, itemOrder: customDeductions.length },
    ]);
  };

  const handleRemoveDeduction = idx => {
    onSettingsChange(
      'customDeductions',
      customDeductions.filter((_, i) => i !== idx),
    );
  };

  const handleDeductionChange = (idx, field, value) => {
    const updated = customDeductions.map((item, i) =>
      i === idx ? { ...item, [field]: value } : item,
    );
    onSettingsChange('customDeductions', updated);
  };

  return (
    <Card>
      <SectionTitle>{t('급여 기본 설정')}</SectionTitle>

      <SectionContent sx={{ mt: '24px' }}>
        {/* 적용 시작일 */}
        <FormRow>
          <FormFieldGroup>
            <FormFieldLabel>{t('적용 시작일')}</FormFieldLabel>
            <FormFieldDesc>{t('설정한 급여의 적용 시작일')}</FormFieldDesc>
          </FormFieldGroup>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <RealInput
              type="date"
              inputWidth="200px"
              textAlign="left"
              value={settings.useHireDateAsStart === 'Y' ? (employee?.hireDate || '') : (settings.effectiveStartDate || '')}
              onChange={e => onSettingsChange('effectiveStartDate', e.target.value || null)}
              disabled={settings.useHireDateAsStart === 'Y'}
              style={settings.useHireDateAsStart === 'Y' ? { backgroundColor: '#F0F0F0' } : {}}
            />
            <CheckRow
              onClick={() => {
                const next = settings.useHireDateAsStart === 'Y' ? 'N' : 'Y';
                onSettingsChange('useHireDateAsStart', next);
                if (next === 'Y') onSettingsChange('effectiveStartDate', null);
              }}
              sx={{ cursor: 'pointer' }}
            >
              <CheckboxCircle isChecked={settings.useHireDateAsStart === 'Y'}>
                {settings.useHireDateAsStart === 'Y' && <CheckIcon sx={{ fontSize: 16 }} />}
              </CheckboxCircle>
              <CheckLabel>{t('입사일과 동일')}</CheckLabel>
            </CheckRow>
          </Box>
        </FormRow>

        <Divider />

        {/* 기본 시급 */}
        <FormRow>
          <FormFieldGroup>
            <FormFieldLabel>{t('기본 시급')}</FormFieldLabel>
            <FormFieldDesc>
              {t(`${MIN_WAGE_YEAR}년 최저시급 : ${MIN_WAGE_AMOUNT.toLocaleString()}원`)}
            </FormFieldDesc>
          </FormFieldGroup>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CommaInput
              inputWidth="200px"
              value={settings.baseAmount}
              onChange={val => onSettingsChange('baseAmount', val)}
            />
            <InputUnit>{t('원')}</InputUnit>
          </Box>
          <CheckRow
            onClick={() => {
              const next = settings.applyMinWage === 'Y' ? 'N' : 'Y';
              onSettingsChange('applyMinWage', next);
              // 체크하면 기준 금액이 최저시급임을 눈으로 확인할 수 있게 입력칸까지 채운다.
              if (next === 'Y') onSettingsChange('baseAmount', MIN_WAGE_AMOUNT);
            }}
            sx={{ cursor: 'pointer' }}
          >
            <CheckboxCircle isChecked={settings.applyMinWage === 'Y'}>
              {settings.applyMinWage === 'Y' && <CheckIcon sx={{ fontSize: 16 }} />}
            </CheckboxCircle>
            <CheckLabel>{t('최저시급 적용')}</CheckLabel>
          </CheckRow>
        </FormRow>

        <Divider />

        {/* 공제 항목 */}
        <FormRow sx={{ alignItems: 'flex-start' }}>
          <FormFieldGroup>
            <FormFieldLabel>{t('공제 항목')}</FormFieldLabel>
            <FormFieldDesc>{t('해당 직원에게 적용할 공제 방식을 선택 및 설정')}</FormFieldDesc>
          </FormFieldGroup>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {DEDUCTION_OPTIONS.map(opt => (
                <DeductionCard
                  key={opt.key}
                  isSelected={deductionType === opt.key}
                  onClick={() => onSettingsChange('deductionType', opt.key)}
                >
                  <DeductionRate isSelected={deductionType === opt.key}>
                    {opt.rate}
                  </DeductionRate>
                  <DeductionLabel isSelected={deductionType === opt.key}>
                    {opt.label}
                  </DeductionLabel>
                </DeductionCard>
              ))}
            </Box>

            {deductionType === 'CUSTOM' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {customDeductions.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <RealInput
                      inputWidth="200px"
                      textAlign="left"
                      placeholder={t('공제 항목명 (예 : 국민연금)')}
                      value={item.itemName}
                      onChange={e => handleDeductionChange(idx, 'itemName', e.target.value)}
                     
                    />
                    <CommaInput
                      inputWidth="80px"
                      placeholder="0.0"
                      value={item.rate}
                      onChange={val => handleDeductionChange(idx, 'rate', val)}
                    />
                    <InputUnit>%</InputUnit>
                    <DeleteOutlineIcon
                      sx={{ fontSize: 20, color: '#A6A6A6', cursor: 'pointer', '&:hover': { color: '#e53935' } }}
                      onClick={() => handleRemoveDeduction(idx)}
                    />
                  </Box>
                ))}
                <AddRangeButton onClick={handleAddDeduction}>
                  <AddCircleOutlineIcon sx={{ fontSize: 24, color: '#4AB3BC' }} />
                  <AddRangeText>{t('항목 추가')}</AddRangeText>
                </AddRangeButton>
              </Box>
            ) : (
              <DeductionDesc>{DEDUCTION_DESCRIPTIONS[deductionType]}</DeductionDesc>
            )}
          </Box>
        </FormRow>
      </SectionContent>
    </Card>
  );
};

// ============================================
// 시간제 수당 설정
// ============================================
// 요율 선택지. 항목별로 같은 값을 쓴다.
const RATE_OPTIONS = ['100%', '150%', '200%'];

// 항목별 기본 요율. 반드시 RATE_OPTIONS 안의 값이어야 한다 —
// 목록에 없는 값을 기본값으로 두면 어느 버튼도 선택 표시가 되지 않는다.
const DEFAULT_RATE = {
  overtime: '150%',
  nightWork: '150%',
  holidayWork: '150%',
  weeklyHoliday: '150%',
};

const OvertimeSettingItem = ({
  title,
  desc,
  enabled,
  onToggle,
  selectedRate,
  onRateChange,
  extraValue,
  onExtraChange,
  baseAmount,
  applyMinWage,
  rateKind,
  noteText,
}) => {
  const { t } = useTranslation();
  const rate = selectedRate || DEFAULT_RATE[rateKind];
  const rateNum = parseInt(rate.replace('%', ''), 10);
  // 최저시급 적용 시 실제 정산과 동일하게 max(기본 시급, 최저시급) 을 안내에 사용
  const hourlyRate =
    applyMinWage === 'Y'
      ? Math.max(baseAmount || 0, MIN_WAGE_AMOUNT)
      : baseAmount || 0;

  // 선택된 요율로 계산한 시간당 금액.
  const rateAmount = Math.floor(hourlyRate * rateNum / 100);

  // 금액을 직접 입력했는지 여부.
  // 직접 입력한 금액은 요율과 무관한 고정값이므로 이때는 어떤 요율 뱃지도 선택 표시되지 않는다.
  // 저장된 설정을 다시 열었을 때도 금액이 요율 계산값과 다르면 직접 입력으로 본다.
  const [isManual, setIsManual] = useState(
    () => (extraValue || 0) > 0 && extraValue !== rateAmount,
  );

  // 요율 뱃지가 선택된 상태에서는 입력칸이 항상 그 요율의 계산값을 보여준다.
  // (시급이나 요율이 바뀌면 금액도 따라 바뀐다)
  useEffect(() => {
    if (enabled !== 'Y' || isManual) return;
    if ((extraValue || 0) !== rateAmount) onExtraChange(rateAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isManual, rateAmount]);

  const activeRate = isManual ? null : rate;

  // 이 수당으로 지급되는 시간당 금액. 입력값이 있으면 그 값이, 없으면 요율 계산값이 쓰인다.
  const allowancePerHour = (extraValue || 0) > 0 ? extraValue : rateAmount;

  // 야간은 근로분이 기본급에 이미 들어 있어, 총액에서 시급을 뺀 차액만 수당으로 지급된다.
  // 요율이 뜻하는 최종 배수는 다른 항목과 같으므로 미리보기 문구도 동일하다.
  const paidWithBase = rateKind === 'nightWork';
  const nightPremium = Math.max(0, allowancePerHour - hourlyRate);

  return (
    <FieldBlock>
      <FormRow sx={{ alignItems: 'flex-start' }}>
        <FormFieldGroup>
          <ToggleFieldRow>
            <FormFieldLabel>{title}</FormFieldLabel>
            <ToggleSwitch checked={enabled === 'Y'} onChange={onToggle} />
          </ToggleFieldRow>
          <FormFieldDesc>{desc}</FormFieldDesc>
        </FormFieldGroup>

        {enabled === 'Y' && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <OvertimeRow>
              <RateButtonGroup>
                {RATE_OPTIONS.map(opt => (
                  <RateButton
                    key={opt}
                    isSelected={activeRate === opt}
                    onClick={() => {
                      // 고른 요율로 계산한 시간당 금액을 옆 칸에 채운다.
                      // 관리자가 실제 지급액을 눈으로 보고 바로 고칠 수 있게 하기 위함이다.
                      setIsManual(false);
                      onRateChange(opt);
                      onExtraChange(Math.floor(hourlyRate * parseInt(opt.replace('%', ''), 10) / 100));
                    }}
                  >
                    {opt}
                  </RateButton>
                ))}
              </RateButtonGroup>
              <InputUnit>{t('시간당')}</InputUnit>
              <CommaInput
                inputWidth="100px"
                value={extraValue}
                onChange={val => {
                  // 직접 입력하면 요율 뱃지 선택이 풀린다. 비우면 다시 요율 계산값으로 돌아간다.
                  setIsManual((val || 0) > 0);
                  onExtraChange(val);
                }}
              />
              <InputUnit>{t('원 지급')}</InputUnit>
            </OvertimeRow>
            {hourlyRate > 0 && (
              <Box sx={{ fontSize: '12px', color: '#888', lineHeight: 1.6 }}>
                {isManual ? (
                  <>
                    시간당 <strong style={{ color: '#4AB3BC' }}>{(extraValue || 0).toLocaleString()}원</strong>
                    {' 고정 지급 · 시급이 바뀌어도 이 금액은 그대로입니다'}
                  </>
                ) : (
                  <>
                    시급 <strong style={{ color: '#4AB3BC' }}>{hourlyRate.toLocaleString()}원</strong>
                    {' × '}{rateNum}{'% = '}
                    <strong style={{ color: '#4AB3BC' }}>{rateAmount.toLocaleString()}원/시간</strong>
                    {' · 시급이나 요율을 바꾸면 금액도 함께 바뀝니다'}
                  </>
                )}
              </Box>
            )}
            {paidWithBase && hourlyRate > 0 && (
              <Box sx={{ fontSize: '12px', color: '#888', lineHeight: 1.6 }}>
                {'이 시간의 근로분 '}{hourlyRate.toLocaleString()}
                {'원은 기본급에 이미 포함되어, 수당으로는 차액 '}
                <strong style={{ color: '#4AB3BC' }}>{nightPremium.toLocaleString()}원</strong>
                {'만 지급됩니다'}
              </Box>
            )}
            {noteText && (
              <Box sx={{ fontSize: '12px', color: '#888', lineHeight: 1.6 }}>{noteText}</Box>
            )}
          </Box>
        )}
      </FormRow>
    </FieldBlock>
  );
};

const HourlyAllowanceSection = ({ settings, onSettingsChange }) => {
  const { t } = useTranslation();
  const ba = settings.baseAmount;

  const toggle = key => onSettingsChange(key, settings[key] === 'Y' ? 'N' : 'Y');

  return (
    <Card>
      <SectionTitle>{t('수당 설정')}</SectionTitle>

      <SectionContent sx={{ mt: '24px' }}>
        <OvertimeSettingItem
          title={t('연장근로 수당')}
          desc={t('1일 8시간, 주 40시간 초과 근무 시')}
          enabled={settings.overtimeEnabled}
          onToggle={() => toggle('overtimeEnabled')}
          selectedRate={settings.overtimeRate || DEFAULT_RATE.overtime}
          rateKind="overtime"
          onRateChange={val => onSettingsChange('overtimeRate', val)}
          extraValue={settings.overtimeExtra}
          onExtraChange={val => onSettingsChange('overtimeExtra', val)}
          baseAmount={ba}
          applyMinWage={settings.applyMinWage}
        />

        <Divider />

        <OvertimeSettingItem
          title={t('야간근로 수당')}
          desc={t('22:00~06:00 근무 시')}
          enabled={settings.nightWorkEnabled}
          onToggle={() => toggle('nightWorkEnabled')}
          selectedRate={settings.nightWorkRate || DEFAULT_RATE.nightWork}
          rateKind="nightWork"
          onRateChange={val => onSettingsChange('nightWorkRate', val)}
          extraValue={settings.nightWorkExtra}
          onExtraChange={val => onSettingsChange('nightWorkExtra', val)}
          baseAmount={ba}
          applyMinWage={settings.applyMinWage}
        />

        <Divider />

        <OvertimeSettingItem
          title={t('휴일근로 수당')}
          desc={t('주말 및 공휴일 근무 시')}
          enabled={settings.holidayWorkEnabled}
          onToggle={() => toggle('holidayWorkEnabled')}
          selectedRate={settings.holidayWorkRate || DEFAULT_RATE.holidayWork}
          rateKind="holidayWork"
          noteText="1일 8시간 초과분은 법정에 따라 50%p 더 가산해야 합니다(150% 선택 시 초과분 200%). 출퇴근 기록 연동 전까지는 자동 적용되지 않으니, 초과분이 있으면 정산 상세의 조정 항목으로 보정하세요."
          onRateChange={val => onSettingsChange('holidayWorkRate', val)}
          extraValue={settings.holidayWorkExtra}
          onExtraChange={val => onSettingsChange('holidayWorkExtra', val)}
          baseAmount={ba}
          applyMinWage={settings.applyMinWage}
        />

        <Divider />

        <OvertimeSettingItem
          title={t('주휴 수당')}
          desc={t('주 15시간 이상 근무 시')}
          enabled={settings.weeklyHolidayEnabled}
          onToggle={() => toggle('weeklyHolidayEnabled')}
          selectedRate={settings.weeklyHolidayRate || DEFAULT_RATE.weeklyHoliday}
          rateKind="weeklyHoliday"
          noteText="월급제는 월 소정근로시간에 주휴가 포함되어 있어 별도로 지급되지 않습니다."
          onRateChange={val => onSettingsChange('weeklyHolidayRate', val)}
          extraValue={settings.weeklyHolidayExtra}
          onExtraChange={val => onSettingsChange('weeklyHolidayExtra', val)}
          baseAmount={ba}
          applyMinWage={settings.applyMinWage}
        />
      </SectionContent>
    </Card>
  );
};

export { HourlyBasicSection, HourlyAllowanceSection };
